import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Lock, CheckCircle2 } from "lucide-react";

const SHARED_KEY = "jnn_lead_unlocked";

/**
 * Shared unlock state across the Calculator and Furniture pages.
 * Once a visitor submits their contact info on either page, both unlock
 * for the rest of the browser session (sessionStorage).
 */
export const useLeadGate = () => {
  const [unlocked, setUnlocked] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SHARED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const unlock = () => {
    setUnlocked(true);
    try {
      sessionStorage.setItem(SHARED_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return { unlocked, unlock };
};

const gateSchema = z.object({
  firstName: z.string().min(1, "Please enter your first name"),
  lastName: z.string().min(1, "Please enter your last name"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  email: z.string().email("Please enter a valid email"),
  consent: z.boolean().optional(),
});

type GateData = z.infer<typeof gateSchema>;

interface LeadGateFormProps {
  onUnlock: () => void;
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  /** Visual variant: "card" (default) sits inside a styled card. */
  variant?: "card" | "inline";
}

/**
 * Contact form that gates premium content (calculator results / furniture list).
 * On submit it fires the LeadConnector tracking event + Google Ads conversion,
 * then calls onUnlock() to reveal the gated content.
 */
export const LeadGateForm = ({
  onUnlock,
  title = "Unlock Your Results",
  subtitle = "Enter your contact info to see your full cash-flow breakdown and furniture list.",
  ctaLabel = "Unlock My Results",
  variant = "card",
}: LeadGateFormProps) => {
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<GateData>({
    resolver: zodResolver(gateSchema),
    defaultValues: { consent: false },
  });

  const onSubmit = async (data: GateData) => {
    setSubmitting(true);

    // Detect Google Ads attribution from URL params
    const searchParams = new URLSearchParams(window.location.search);
    const gclid = searchParams.get("gclid") || "";
    const isGoogleAds = !!gclid || searchParams.get("utm_source") === "google";

    const tags: string[] = [
      data.consent ? "sms-consent-yes" : "sms-consent-no",
      "calculator-gate",
    ];
    if (isGoogleAds) tags.push("google-ads");

    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "JNN Homes Calculator Gate",
      formData: {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone,
        "contact.consent": data.consent ? "sms-consent-yes" : "sms-consent-no",
        ...(gclid ? { gclid } : {}),
      },
      formLabels: {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email",
        phone: "Phone Number",
        "contact.consent": "Consent",
        ...(gclid ? { gclid: "Google Click ID" } : {}),
      },
      tags,
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: "tk_e593ff1735ab4894b669bd44863e3f62",
      locationId: "zN4DJbB2mVOEGrPHHphx",
      sessionId: crypto.randomUUID(),
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
      },
    };

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        version: "2021-07-28",
      },
      body: JSON.stringify(trackingPayload),
    }).catch(() => {});

    // Google Ads lead conversion
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "conversion", {
        send_to: "AW-18543046429/MCENCPLwmK0dEJ3egYpF",
      });
    }
    // Meta Pixel lead event
    if (typeof window !== "undefined" && (window as any).fbq) {
      (window as any).fbq("track", "Lead", {
        content_name: "Calculator gate",
      });
    }

    // Reveal content immediately
    onUnlock();
    setSubmitting(false);
  };

  const wrapperClass =
    variant === "card"
      ? "bg-card border-2 border-primary/20 rounded-2xl shadow-xl p-6 md:p-8"
      : "";

  return (
    <div className={wrapperClass}>
      {(title || subtitle) && (
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
            <Lock className="w-6 h-6" />
          </div>
          {title && (
            <h3 className="text-2xl font-heading font-bold mb-2">{title}</h3>
          )}
          {subtitle && (
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {subtitle}
            </p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gate-firstName">First Name</Label>
            <Input
              id="gate-firstName"
              {...register("firstName")}
              className="bg-muted/50"
            />
            {errors.firstName && (
              <p className="text-xs text-destructive">
                {errors.firstName.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="gate-lastName">Last Name</Label>
            <Input
              id="gate-lastName"
              {...register("lastName")}
              className="bg-muted/50"
            />
            {errors.lastName && (
              <p className="text-xs text-destructive">
                {errors.lastName.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="gate-phone">Phone Number *</Label>
          <Input
            id="gate-phone"
            type="tel"
            {...register("phone")}
            className="bg-muted/50"
          />
          {errors.phone && (
            <p className="text-xs text-destructive">{errors.phone.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="gate-email">Email *</Label>
          <Input
            id="gate-email"
            type="email"
            {...register("email")}
            className="bg-muted/50"
          />
          {errors.email && (
            <p className="text-xs text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox
            id="gate-consent"
            onCheckedChange={(checked) => setValue("consent", checked === true)}
          />
          <label
            htmlFor="gate-consent"
            className="text-[10px] text-muted-foreground leading-relaxed"
          >
            I consent to receive SMS from JNN Management, LLC DBA JNN Homes (a
            Virginia Limited Liability Company) regarding management services.
            Message frequency varies. Msg & data rates may apply. Reply STOP to
            opt out, HELP for help. See our{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-primary"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-primary"
            >
              Terms
            </a>
            .
          </label>
        </div>

        <Button
          type="submit"
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-5 text-base font-bold"
          disabled={submitting}
        >
          {submitting ? "Unlocking..." : ctaLabel}
        </Button>

        <p className="text-[10px] text-center text-muted-foreground">
          We'll contact you within 24 hours. Your information is never shared.
        </p>
      </form>
    </div>
  );
};

/**
 * Convenience wrapper: shows the gate form until unlocked, then renders children.
 */
interface LeadGateProps {
  unlocked: boolean;
  onUnlock: () => void;
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  variant?: "card" | "inline";
}

export const LeadGate = ({
  unlocked,
  onUnlock,
  children,
  title,
  subtitle,
  ctaLabel,
  variant,
}: LeadGateProps) => {
  if (unlocked) return <>{children}</>;
  return (
    <LeadGateForm
      onUnlock={onUnlock}
      title={title}
      subtitle={subtitle}
      ctaLabel={ctaLabel}
      variant={variant}
    />
  );
};

export const UnlockedBadge = () => (
  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1">
    <CheckCircle2 className="w-3.5 h-3.5" /> Unlocked
  </div>
);
